// The engine behind a style guide's tunable tokens, with no framework and
// nothing project-specific: which tokens can be changed, which values are
// safe, applying them, and the link that carries a proposal. A project hands
// it its own token list, font and shadow choices, and presets.
//
// Copied from the DSE Nexus prototype (src/styleguide/), where it started.
// One addition: `root: false`, for a component library that owns its tokens.

export type TokenKind = 'font' | 'type' | 'color' | 'radius' | 'shadow' | 'size'

export interface TokenSpec {
  name: string
  /** What the token is for, in a few words. */
  use: string
  /** The seed a derived value is mixed from; derived tokens can't be edited. */
  follows?: string
  /** The contrast this colour needs on white, shown and checked: 4.5 for
   *  text, 3 for icons and markers (WCAG 2). */
  contrast?: number
  /** For "type" tokens: a size in px, or a unitless ratio. */
  unit?: 'px' | 'ratio'
}

export interface TokenGroup {
  title: string
  kind: TokenKind
  tokens: TokenSpec[]
}

export interface FontChoice {
  id: string
  label: string
  value: string
}

export interface ShadowChoice {
  id: string
  label: string
  /** Token name → CSS box-shadow. */
  values: Record<string, string>
}

/**
 * Short, link-safe values per token: a colour as "4c6a80", a radius in px as
 * "6", a font or shadow as its choice id. Nothing else is accepted, so a
 * shared link can't inject CSS (a free-form value could, say, load an image
 * from another site).
 */
export type Overrides = Record<string, string>

export interface Preset {
  id: string
  label: string
  values: Overrides
  /** A few words under the label, e.g. "14 px · ×1.2". */
  note?: string
  /** Colours to show as dots (hex without "#"), for colour options. */
  swatch?: string[]
}

/**
 * One way the look can feel different (colour, type, corners, depth, font),
 * with a few ready options. Picking one replaces the dimension's tokens and
 * leaves the rest alone, so feels combine freely.
 */
export interface Dimension {
  id: string
  label: string
  /** Every token any option sets; picking an option clears the others. */
  tokens: string[]
  /** The first option is the stylesheet as written (no values). */
  options: Preset[]
}

export interface StyleGuideConfig {
  /** Custom property prefix, e.g. "--nexus-". */
  prefix: string
  /** False: leave :root alone and let the project build its theme from the
   *  overrides (see `subscribe`). A library that sets its own variables on
   *  its own element, as Fluent's FluentProvider does, shadows anything set
   *  on :root, so writing there would change nothing. Default true. */
  root?: boolean
  groups: TokenGroup[]
  /** The first choice is the stylesheet's own value. */
  fonts: FontChoice[]
  /** The first choice is the stylesheet's own value. */
  shadows: ShadowChoice[]
  /** Ready options per feel; see Dimension. */
  dimensions: Dimension[]
  /** Whole looks, usually one option of each dimension. One with no values is
   *  the stylesheet as written. */
  presets: Preset[]
}

const SAFE_COLOR = /^[0-9a-f]{6}$/i
const SAFE_PX = /^\d{1,2}$/
const SAFE_RATIO = /^[12](\.\d{1,3})?$/
/** Smallest type size accepted, in px. */
export const MIN_TEXT_PX = 10

const sameValues = (a: Overrides, b: Overrides) => {
  const keys = Object.keys(a)
  return keys.length === Object.keys(b).length && keys.every((key) => a[key] === b[key])
}

export const createStyleGuide = (config: StyleGuideConfig) => {
  const writesRoot = config.root !== false
  // Editable tokens and their kinds. Derived tokens and sizes stay read-only.
  const kindOf: Record<string, TokenKind> = {}
  const unitOf: Record<string, TokenSpec['unit']> = {}
  config.groups.forEach((group) =>
    group.tokens.forEach((token) => {
      if (!token.follows && group.kind !== 'size') kindOf[token.name] = group.kind
      unitOf[token.name] = token.unit
    }),
  )

  const isValid = (name: string, short: string) => {
    switch (kindOf[name]) {
      case 'color':
        return SAFE_COLOR.test(short)
      case 'radius':
        return SAFE_PX.test(short)
      case 'type':
        // A ratio from 1 to 2 (e.g. 1.2); a size of at least MIN_TEXT_PX, so
        // steps derived below it (e.g. base - 2px) stay real font sizes.
        return unitOf[name] === 'ratio'
          ? SAFE_RATIO.test(short) && Number(short) >= 1 && Number(short) <= 2
          : SAFE_PX.test(short) && Number(short) >= MIN_TEXT_PX
      case 'font':
        return config.fonts.some((font) => font.id === short)
      case 'shadow':
        return config.shadows.some((shadow) => shadow.id === short && name in shadow.values)
      default:
        return false
    }
  }

  const cssValue = (name: string, short: string) => {
    switch (kindOf[name]) {
      case 'color':
        return `#${short.toLowerCase()}`
      case 'radius':
        return `${short}px`
      case 'type':
        return unitOf[name] === 'ratio' ? short : `${short}px`
      case 'font':
        return config.fonts.find((font) => font.id === short)?.value ?? ''
      case 'shadow':
        return config.shadows.find((shadow) => shadow.id === short)?.values[name] ?? ''
      default:
        return ''
    }
  }

  const onlyValid = (overrides: Overrides) => {
    const out: Overrides = {}
    Object.keys(overrides).forEach((name) => {
      if (isValid(name, overrides[name]!)) out[name] = overrides[name]!
    })
    return out
  }

  // What is applied now. Kept here, not in a page, so it survives moving
  // between pages: a reviewer can tune here and look at the whole site.
  let current: Overrides = {}
  // Everyone showing the overrides (a page, a drawer) hears each change.
  const listeners = new Set<(overrides: Overrides) => void>()

  /** Apply these overrides, replacing the previous ones. */
  const apply = (next: Overrides) => {
    const style = document.documentElement.style
    if (writesRoot) Object.keys(current).forEach((name) => style.removeProperty(config.prefix + name))
    current = onlyValid(next)
    if (writesRoot)
      Object.keys(current).forEach((name) =>
        style.setProperty(config.prefix + name, cssValue(name, current[name]!)),
      )
    listeners.forEach((listener) => listener(current))
    return current
  }

  /** Hear every apply(); returns the way to stop. */
  const subscribe = (listener: (overrides: Overrides) => void) => {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }

  /** "primary.4c6a80~radius-md.6": only [0-9a-z.~-], so safe in a URL. */
  const encode = (overrides: Overrides) =>
    Object.keys(overrides)
      .map((name) => `${name}.${overrides[name]}`)
      .join('~')

  const decode = (text: string | null): Overrides => {
    const out: Overrides = {}
    ;(text ?? '').split('~').forEach((pair) => {
      const dot = pair.indexOf('.')
      if (dot > 0) out[pair.slice(0, dot)] = pair.slice(dot + 1)
    })
    return onlyValid(out)
  }

  /** The changed tokens as a :root block, ready for the stylesheet. */
  const toCss = (overrides: Overrides) =>
    Object.keys(overrides).length
      ? `:root {\n${Object.keys(overrides)
          .map((name) => `  ${config.prefix}${name}: ${cssValue(name, overrides[name]!)};`)
          .join('\n')}\n}\n`
      : ''

  const presetOf = (overrides: Overrides) =>
    config.presets.find((preset) => sameValues(preset.values, overrides))

  /** The overrides with one dimension switched to an option; others untouched. */
  const withOption = (overrides: Overrides, dimension: Dimension, option: Preset) => {
    const next: Overrides = {}
    Object.keys(overrides).forEach((name) => {
      if (!dimension.tokens.includes(name)) next[name] = overrides[name]!
    })
    return { ...next, ...option.values }
  }

  /** Which option a dimension is on now, if any (else it's been hand-tuned). */
  const optionOf = (overrides: Overrides, dimension: Dimension) => {
    const own: Overrides = {}
    dimension.tokens.forEach((name) => {
      if (name in overrides) own[name] = overrides[name]!
    })
    return dimension.options.find((option) => sameValues(option.values, own))
  }

  return {
    config,
    kind: (name: string): TokenKind | undefined => kindOf[name],
    unit: (name: string) => unitOf[name],
    isValid,
    cssValue,
    apply,
    subscribe,
    current: () => current,
    encode,
    decode,
    toCss,
    presetOf,
    withOption,
    optionOf,
  }
}

export type StyleGuide = ReturnType<typeof createStyleGuide>
